import { FC } from "react"
import { Box } from "@mui/material"
import { WEEKDAY_NAMES_SHORT } from "../../model/mskCalendar"
import { DayColumn, HeaderTiers } from "../../model/period"
import {
  DAY_COL_W,
  HEADER_H,
  HEADER_TIER_H,
  STAFF_COL_W,
  TOTAL_COL_W,
  Z,
  dayBorderLeftSx,
  gridBorderColor,
  gridBorderColorStrong,
  weekendBg,
} from "./gridGeometry"

interface GridHeaderProps {
  days: DayColumn[]
  tiers: HeaderTiers
}

const tierSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderBottom: "1px solid",
  borderColor: gridBorderColor,
  overflow: "hidden",
  whiteSpace: "nowrap",
} as const

/**
 * Шапка из четырёх ярусов РАЗНОЙ высоты: месяцы по неделям → «Неделя N» → Пн…Вс → числа.
 * Залипает целиком (sticky top), углы — свои sticky-слои внутри её контекста наложения.
 */
export const GridHeader: FC<GridHeaderProps> = ({ days, tiers }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: Z.header,
      display: "flex",
      height: HEADER_H,
      bgcolor: "background.paper", // непрозрачный фон обязателен
    }}
  >
    <Box
      sx={{
        position: "sticky",
        left: 0,
        zIndex: Z.stickyInHeader,
        width: STAFF_COL_W,
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        px: 1,
        fontWeight: 600,
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderBottom: "1px solid",
        borderColor: gridBorderColor,
        borderRightColor: gridBorderColorStrong,
      }}
    >
      Сотрудник
    </Box>

    <Box sx={{ flex: "0 0 auto" }}>
      {/* Ярус 1: месяцы — по одному блоку на неделю, подписи не сливаются */}
      <Box sx={{ display: "flex", height: HEADER_TIER_H[0] }}>
        {tiers.weeks.map((w, i) => (
          <Box
            key={w.isoWeek}
            sx={{
              ...tierSx,
              width: 7 * DAY_COL_W,
              fontWeight: 600,
              // граница между неделями усилена; слева от первой недели уже есть
              // borderRight залипшей колонки сотрудника — вторую линию не рисуем
              borderLeft: i === 0 ? "none" : "2px solid",
              borderLeftColor: gridBorderColorStrong,
            }}
          >
            {w.monthLabel}
          </Box>
        ))}
      </Box>
      {/* Ярус 2: номера ISO-недель */}
      <Box sx={{ display: "flex", height: HEADER_TIER_H[1] }}>
        {tiers.weeks.map((w, i) => (
          <Box
            key={w.isoWeek}
            sx={{
              ...tierSx,
              width: 7 * DAY_COL_W,
              fontSize: 12,
              borderLeft: i === 0 ? "none" : "2px solid",
              borderLeftColor: gridBorderColorStrong,
            }}
          >
            Неделя {w.isoWeek}
          </Box>
        ))}
      </Box>
      {/* Ярус 3: дни недели */}
      <Box sx={{ display: "flex", height: HEADER_TIER_H[2] }}>
        {days.map((d) => (
          <Box
            key={d.key}
            sx={{
              ...tierSx,
              width: DAY_COL_W,
              fontSize: 12,
              // выходные не приглушаются, а выделяются: та же подложка, что у ячеек
              fontWeight: d.isWeekend ? 700 : 400,
              color: "text.primary",
              bgcolor: d.isWeekend ? weekendBg : undefined,
              ...dayBorderLeftSx(d.weekday),
            }}
          >
            {WEEKDAY_NAMES_SHORT[d.weekday - 1]}
          </Box>
        ))}
      </Box>
      {/* Ярус 4: числа месяца */}
      <Box sx={{ display: "flex", height: HEADER_TIER_H[3] }}>
        {days.map((d) => (
          <Box
            key={d.key}
            sx={{
              ...tierSx,
              width: DAY_COL_W,
              fontSize: 14,
              fontWeight: d.isToday || d.isWeekend ? 700 : 400,
              color: "text.primary",
              bgcolor: d.isWeekend ? weekendBg : undefined,
              ...dayBorderLeftSx(d.weekday),
            }}
          >
            {d.day}
          </Box>
        ))}
      </Box>
    </Box>

    <Box
      sx={{
        position: "sticky",
        right: 0,
        zIndex: Z.stickyInHeader,
        width: TOTAL_COL_W,
        flex: "0 0 auto",
        bgcolor: "background.paper",
        borderLeft: "1px solid",
        borderBottom: "1px solid",
        borderColor: gridBorderColor,
        borderLeftColor: gridBorderColorStrong,
      }}
    >
      <Box sx={{ ...tierSx, height: HEADER_TIER_H[0], fontWeight: 600 }}>
        {tiers.yearLabel}
      </Box>
      <Box
        sx={{ ...tierSx, height: HEADER_H - HEADER_TIER_H[0], fontSize: 12 }}
      >
        Всего дней
      </Box>
    </Box>
  </Box>
)
